import React, { useCallback } from 'react';
import Link from '@atlaskit/link';
import { fg } from '@atlassian/jira-feature-gating';
import {
	dismissFlag as createDismissFlag,
	type FlagComponentProps,
	ErrorFlag,
	useFlagService,
} from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { ProductDisplayNames, type CrossJoinTargetProduct } from '../../common/types.tsx';
import { generateId } from '../../utils/index.tsx';
import { messages } from './messages.tsx';

export const useRequestAccessErrorFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	return useCallback(
		(product: CrossJoinTargetProduct) => {
			const id = `error-${generateId()}`;
			const dismiss = () => dismissFlag(createDismissFlag(id));

			showFlag({
				id,
				render: (props) => (
					<RequestAccessErrorFlag {...props} id={id} product={product} onDismiss={dismiss} />
				),
			});
		},
		[dismissFlag, showFlag],
	);
};

export type Props = {
	id: FlagComponentProps['id'];
	product: CrossJoinTargetProduct;
	onDismiss: FlagComponentProps['onDismissed'];
};

const JIRA_SUPPORT_LINK = 'https://support.atlassian.com/contact/';

export const RequestAccessErrorFlag = ({ onDismiss, ...props }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ErrorFlag
			{...props}
			onDismissed={onDismiss}
			title={formatMessage(messages.title, {
				productDisplayName: ProductDisplayNames[props.product],
			})}
			description={
				<>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>
						{formatMessage(messages.description)}
						{fg('replace-anchor-with-link__groot-growth') ? (
							<Link href={JIRA_SUPPORT_LINK}>{formatMessage(messages.action)}</Link>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a href={JIRA_SUPPORT_LINK}>{formatMessage(messages.action)}</a>

							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
						)}
						.
					</p>
				</>
			}
		/>
	);
};
